/**
 * SchemaHelperSidebar — collapsible, resizable field list panel for the SQL editor.
 *
 * Reuses the same entity-grouped layout as the FieldPickerPopover:
 * fields grouped by semantic entity, with secondary Measures/Dimensions/Dates
 * sections when an entity has mixed types. Clicking a field inserts its name
 * into the SQL editor.
 *
 * The panel width is adjustable via a drag handle on the right edge.
 */
import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  makeStyles,
  tokens,
  Button,
  Text,
  Input,
  mergeClasses,
} from '@fluentui/react-components';
import {
  ChevronLeftRegular,
  ChevronRightRegular,
  SearchRegular,
} from '@fluentui/react-icons';
import type { DatasetViewColumn } from '../../../api/types';
import EntityGroup from '../fieldMapping/fieldPicker/EntityGroup';
import { filterEntityFields, groupByEntity } from '../fieldMapping/fieldPicker/entityFields';
import { useEditorDisplayNames } from '../EditorDisplayNamesContext';

interface SchemaHelperSidebarProps {
  fields: DatasetViewColumn[];
  onFieldClick: (fieldName: string) => void;
}

const MIN_WIDTH = 160;
const MAX_WIDTH = 400;
const DEFAULT_WIDTH = 280;

const useStyles = makeStyles({
  wrapper: {
    display: 'flex',
    height: '100%',
    flexShrink: 0,
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: tokens.colorNeutralBackground2,
    overflow: 'hidden',
    minHeight: 0,
    height: '100%',
    flex: 1,
  },
  collapsed: {
    display: 'flex',
    flexDirection: 'column',
    width: '32px',
    minWidth: '32px',
    height: '100%',
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  resizeHandle: {
    width: '4px',
    cursor: 'col-resize',
    backgroundColor: 'transparent',
    flexShrink: 0,
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
    ':hover': {
      backgroundColor: tokens.colorBrandBackground2,
    },
  },
  resizeHandleActive: {
    backgroundColor: tokens.colorBrandStroke1,
  },
  toggleButton: {
    margin: '4px',
    minWidth: 'unset',
  },
  searchBox: {
    padding: '4px 6px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  fieldList: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: '0',
  },
  collapsedLabel: {
    writingMode: 'vertical-rl',
    textOrientation: 'mixed',
    fontSize: '11px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground3,
    letterSpacing: '1px',
    textTransform: 'uppercase' as const,
    padding: '8px 0',
    textAlign: 'center' as const,
    cursor: 'pointer',
  },
});

const SchemaHelperSidebar: React.FC<SchemaHelperSidebarProps> = ({
  fields,
  onFieldClick,
}) => {
  const styles = useStyles();
  const labels = useEditorDisplayNames();
  const [isExpanded, setIsExpanded] = useState(true);
  const [search, setSearch] = useState('');

  const toggle = useCallback(() => setIsExpanded((v) => !v), []);

  // Filter by search
  const filtered = useMemo(() => filterEntityFields(fields, search, labels), [fields, search, labels]);

  // Group by entity
  const groups = useMemo(() => groupByEntity(filtered, labels), [filtered, labels]);
  const hasEntityData = fields.some((f) => f.source_entity);

  // ── Resize logic ──────────────────────────────────────────────────────
  const [width, setWidth] = useState(DEFAULT_WIDTH);
  const [isResizing, setIsResizing] = useState(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(DEFAULT_WIDTH);
  const cleanupRef = useRef<(() => void) | null>(null);

  // Clean up resize listeners on unmount to prevent leaks
  useEffect(() => {
    return () => cleanupRef.current?.();
  }, []);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    startXRef.current = e.clientX;
    startWidthRef.current = width;

    const handleMouseMove = (ev: MouseEvent) => {
      const delta = ev.clientX - startXRef.current;
      const newWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, startWidthRef.current + delta));
      setWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      cleanupRef.current = null;
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    cleanupRef.current = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [width]);

  // ── Collapsed state ───────────────────────────────────────────────────
  if (!isExpanded) {
    return (
      <div className={styles.collapsed}>
        <Button
          className={styles.toggleButton}
          appearance="subtle"
          size="small"
          icon={<ChevronRightRegular />}
          onClick={toggle}
          title="Expand schema"
        />
        <Text
          className={styles.collapsedLabel}
          onClick={toggle}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              toggle();
            }
          }}
        >
          Fields
        </Text>
      </div>
    );
  }

  // ── Expanded state ────────────────────────────────────────────────────
  return (
    <div className={styles.wrapper} style={{ width }}>
      <div className={styles.container}>
        <Button
          className={styles.toggleButton}
          appearance="subtle"
          size="small"
          icon={<ChevronLeftRegular />}
          onClick={toggle}
          title="Collapse schema"
        />
        <div className={styles.searchBox}>
          <Input
            size="small"
            placeholder="Search..."
            contentBefore={<SearchRegular />}
            value={search}
            onChange={(_, data) => setSearch(data.value)}
            appearance="underline"
          />
        </div>
        <div className={styles.fieldList}>
          {hasEntityData ? (
            groups.map((g) => (
              <EntityGroup
                key={g.entity}
                label={g.label}
                fields={g.fields}
                onSelect={onFieldClick}
                hideHeader={groups.length === 1}
              />
            ))
          ) : (
            <EntityGroup
              label=""
              fields={filtered}
              onSelect={onFieldClick}
              hideHeader
            />
          )}
        </div>
      </div>
      <div
        className={mergeClasses(styles.resizeHandle, isResizing && styles.resizeHandleActive)}
        onMouseDown={handleMouseDown}
      />
    </div>
  );
};

export default SchemaHelperSidebar;